import type { z } from "zod/v4";
import { ClientApiFeaturePathParams, TenantPathParams } from "../index";
import type { QuerySchema, ResourceConfig, ResourceParams, ResourceScope } from "../resource";
import type { EndpointConfig, HttpMethod, ScopedPathParams } from "../endpoint";

/**
 * Wire format for base path params (all strings).
 */
export type BasePathParamsWire = z.input<typeof ClientApiFeaturePathParams>;

/**
 * Query key structure with wire-encoded path and query.
 * Uses simple string types for path since that's what we get after encoding.
 * Includes scope for proper cache separation between camera and tenant scoped resources.
 */
export interface ClientApiQueryKeyWire<TFeature extends string, TResource extends string, TPathWire, TQueryWire> {
  feature: TFeature;
  resource: TResource;
  scope: ResourceScope;
  path: TPathWire;
  query?: TQueryWire;
}

/**
 * Creates a query key from an endpoint and params, encoding to wire format.
 * Returns a query key with wire-encoded (string) values for path and query params.
 */
export function createQueryKey<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    HttpMethod,
    z.ZodType,
    z.ZodType | undefined,
    TQuerySchema
  >,
  params: {
    path: ScopedPathParams<TScope> & z.output<TResourcePathSchema>;
    query?: (TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined) | undefined;
  },
) {
  const { resource, querySchema } = endpoint;
  const scope = resource.scope ?? "camera";

  // Encode base path params to wire format based on scope
  const basePathWire =
    scope === "tenant" ? TenantPathParams.encode(params.path) : ClientApiFeaturePathParams.encode(params.path);

  // Encode extra resource path params if schema exists
  const extraPathWire = resource.resourcePathSchema ? resource.resourcePathSchema.encode(params.path) : {};

  // Encode query params if schema exists
  const queryWire = params.query && querySchema ? querySchema.encode(params.query) : undefined;

  return {
    feature: resource.feature,
    resource: resource.resource,
    scope,
    path: { ...basePathWire, ...extraPathWire },
    query: queryWire,
  };
}
